import React, { useEffect, useState, Fragment } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "tss-react/mui";
import Link from "@mui/material/Link";
import GiftAFriendViaEmail from "./GiftAFriendViaEmail";
import config from "../../utils/config";
import axios from "axios";

// MUI components
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Alert } from "@mui/material";
import { CircularProgress } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import RedeemIcon from "@mui/icons-material/Redeem";

// Redux components
import { connect } from "react-redux";
import {
  getUserData,
  updateUserCredentials,
} from "../../redux/actions/userActions";

const useStyles = makeStyles()((theme) => ({
  parentDiv: {
    display: "flex",
    justifyContent: "center",
  },
  goldenTicket: {
    position: "relative",
    overflow: "hidden",
    padding: 20,
    textAlign: "center",
    border: "4px dashed #FFD700",
    borderRadius: 25,
    background: "linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)",
    width: "100%",
    margin: "auto",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "50%",
      height: "100%",
      background:
        "linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%)",
      transform: "skewX(-25deg)",
      animation: "$glare 2s infinite",
    },
  },
  ticketTitle: {
    fontFamily: '"Cursive", sans-serif',
    color: "#DAA520",
    marginBottom: theme.spacing(1),
  },
  inviteLink: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    position: "relative",
  },
  progress: {
    position: "absolute",
  },
  alert: {
    marginTop: theme.spacing(1),
  },
  "@keyframes glare": {
    "0%": {
      left: "-100%",
      opacity: 0,
    },
    "20%": {
      left: "-50%",
      opacity: 1,
    },
    "50%": {
      left: "100%",
      opacity: 1,
    },
    "80%": {
      left: "150%",
      opacity: 1,
    },
    "100%": {
      left: "200%",
      opacity: 0,
    },
  },
  earningsText: {
    fontStyle: "italic",
    marginTop: theme.spacing(1),
    display: "flex",

    justifyContent: "center",
  },
  redeemIcon: {
    marginRight: 5,
  },
}));

function ReferralsBlock(props) {
  const { classes } = useStyles();
  const {
    user: {
      credentials: { referralCode, handle },
      loading,
      authenticated,
    },
  } = props;

  const [referralProfile, setReferralProfile] = useState(null);
  const [alertText, setAlertText] = useState("");
  // New local loading state for referral code
  const [referralLoading, setReferralLoading] = useState(false);

  useEffect(() => {
    if (props.user.authenticated && props.user.credentials.referralCode) {
      axios
        .get(`/api/referrals/getReferralProfile/${referralCode}`)
        .then((res) => {
          if (res.data) {
            setReferralProfile(res.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching referral profile: ", err);
        });
    }
  }, [props.user.credentials.referralCode]);

  useEffect(() => {
    if (!referralCode) {
      let retryCount = 0;
      const maxRetries = 20;
      setReferralLoading(true); // Start loading
      const attemptGetReferralCode = () => {
        axios
          .get("/api/referrals/getReferralCode")
          .then((res) => {
            props.updateUserCredentials({
              referralCode: res.data,
            });
            setReferralLoading(false); // Stop loading on success
          })
          .catch((err) => {
            if (err.response && err.response.status === 400) {
              retryCount++;
              if (retryCount <= maxRetries) {
                setTimeout(() => {
                  attemptGetReferralCode();
                }, 5000);
              } else {
                setReferralLoading(false); // Stop loading after max retries
                console.error(
                  `Failed to get referral code after ${maxRetries} attempts. Giving up.`
                );
              }
            } else {
              setReferralLoading(false); // Stop loading on other errors
              console.error("Error fetching referral code:", err);
            }
          });
      };
      attemptGetReferralCode();
    }
  }, []);

  function handleLinkCopyClick() {
    navigator.clipboard.writeText(
      config.configName == "Test"
        ? `http://localhost:2001/referral?cgreferral=${referralCode}`
        : `https://contentgenerator.io/referral?cgreferral=${referralCode}`
    );
    setAlertText("Link copied to clipboard");
    setTimeout(() => {
      setAlertText("");
    }, 3000);
  }

  let content;

  content = (
    <div className={classes.parentDiv}>
      <Paper className={classes.goldenTicket}>
        <Typography variant="h3" className={classes.ticketTitle}>
          Gift a month, get a month
        </Typography>
        <Typography variant="body1">
          Gift 1 month of Content Generator to a friend and get 1 month for free
          when they join.
          <Tooltip title="The referred user will get 1 month of the Starter plan. The referring user will receive a $16.99 credit to be used against their next payment.">
            <HelpOutlineIcon fontSize="small" style={{ marginLeft: 5 }} />
          </Tooltip>
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          onClick={handleLinkCopyClick}
          fullWidth
          className={classes.inviteLink}
          disabled={
            loading || !authenticated || !referralCode || referralLoading
          }
        >
          Copy invite link
          {(loading || referralLoading) && (
            <CircularProgress size={25} className={classes.progress} />
          )}
        </Button>

        <GiftAFriendViaEmail
          disabled={loading || referralLoading}
          referralLoading={referralLoading}
        />
        {alertText && (
          <Alert severity="info" className={classes.alert}>
            {alertText}
          </Alert>
        )}
        {referralProfile && (
          <Typography className={classes.earningsText}>
            <RedeemIcon className={classes.redeemIcon} color="primary" />$
            {`${referralProfile.referredSales * 16.99} earned so far.`}
          </Typography>
        )}
      </Paper>
    </div>
  );

  return content;
}

ReferralsBlock.propTypes = {
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, { getUserData, updateUserCredentials })(
  ReferralsBlock
);
